import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Hreflang } from "@/i18n/LanguageContext";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <Helmet>
        <title>Politique de Confidentialité | DevisMaison.be</title>
        <meta name="description" content="Politique de confidentialité de DevisMaison.be - Comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD." />
        <link rel="canonical" href="https://devismaison.be/politique-de-confidentialite" />
      </Helmet>
      <Hreflang frPath="/politique-de-confidentialite" nlPath="/nl/privacybeleid" />
      <Navbar />

      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
            Politique de Confidentialité
          </h1>
          
          <p className="text-slate-600 mb-8">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })}
          </p>

          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                DevisMaison.be (ci-après "nous", "notre" ou "la Plateforme") s'engage à protéger la vie privée des utilisateurs de son site web. La présente politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD - Règlement UE 2016/679) et à la loi belge du 30 juillet 2018 relative à la protection des personnes physiques à l'égard des traitements de données à caractère personnel.
              </p>
              <p className="text-slate-600 leading-relaxed">
                En utilisant notre site web et nos services, vous acceptez les pratiques décrites dans cette politique de confidentialité.
              </p>
            </section>

            {/* Responsable du traitement */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                2. Responsable du traitement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Le responsable du traitement des données personnelles est :
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Propriétaire du site : Stéphane Muller (personne physique)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant : Muller Construction SRL</p>
                <p className="text-slate-600">N° BCE / TVA : BE 0803.324.50</p>
                <p className="text-slate-600">Siège social : Bertrix, Belgique</p>
                <p className="text-slate-600">Téléphone : +32 61 46 83 32</p>
                <p className="text-slate-600">Email : contact@devismaison.be</p>
                <p className="text-slate-600">Site web : www.devismaison.be</p>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Stéphane Muller, en tant que propriétaire du site, se réserve le droit de changer d'exploitant à tout moment. En cas de changement d'exploitant, le nouveau responsable du traitement des données sera communiqué aux utilisateurs via une mise à jour de la présente politique.
              </p>
            </section>

            {/* Données collectées */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                3. Données personnelles collectées
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Nous collectons les catégories de données personnelles suivantes :
              </p>
              
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.1 Données des particuliers (demandeurs de devis)
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Données d'identification : prénom, nom</li>
                <li>Coordonnées : adresse email, numéro de téléphone</li>
                <li>Données de localisation : code postal, ville</li>
                <li>Données relatives au projet : type de travaux, description du projet, budget estimé, délai souhaité</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.2 Données des entrepreneurs
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Données d'identification de l'entreprise : nom de l'entreprise, numéro de TVA</li>
                <li>Coordonnées professionnelles : adresse email, numéro de téléphone</li>
                <li>Données d'authentification : mot de passe (stocké de manière cryptée)</li>
                <li>Données commerciales : zones d'intervention, types de travaux proposés</li>
                <li>Donnees de paiement : virement bancaire SEPA (nous conservons la reference et le montant de la transaction ; aucune donnee de carte bancaire n'est collectee)</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                3.3 Données de navigation
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Adresse IP</li>
                <li>Type de navigateur et système d'exploitation</li>
                <li>Pages visitées et durée de visite</li>
                <li>Cookies techniques et analytiques (voir section 8)</li>
              </ul>
            </section>

            {/* Finalités */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                4. Finalités du traitement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Vos données personnelles sont traitées pour les finalités suivantes :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Mise en relation :</strong> transmettre vos demandes de devis aux entrepreneurs correspondants</li>
                <li><strong>Gestion des comptes entrepreneurs :</strong> création, authentification et gestion des comptes professionnels</li>
                <li><strong>Traitement des paiements :</strong> gestion des transactions pour l'achat de leads</li>
                <li><strong>Communication :</strong> envoi de notifications relatives à vos demandes ou votre compte</li>
                <li><strong>Amélioration des services :</strong> analyse statistique pour améliorer notre plateforme</li>
                <li><strong>Obligations légales :</strong> respect de nos obligations légales et fiscales</li>
              </ul>
            </section>

            {/* Base légale */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                5. Base légale du traitement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Conformément à l'article 6 du RGPD, nous traitons vos données sur les bases légales suivantes :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Exécution du contrat (Art. 6.1.b) :</strong> le traitement est nécessaire pour fournir nos services de mise en relation</li>
                <li><strong>Consentement (Art. 6.1.a) :</strong> pour l'envoi de communications marketing (si applicable)</li>
                <li><strong>Intérêt légitime (Art. 6.1.f) :</strong> pour améliorer nos services et prévenir la fraude</li>
                <li><strong>Obligation légale (Art. 6.1.c) :</strong> pour respecter nos obligations comptables et fiscales</li>
              </ul>
            </section>

            {/* Destinataires */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                6. Destinataires des données
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Vos données personnelles peuvent être partagées avec :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Entrepreneurs partenaires :</strong> les coordonnées des demandeurs de devis sont transmises aux entrepreneurs ayant acheté le lead</li>
                <li><strong>Prestataires de services :</strong>
                  <ul className="list-circle pl-6 mt-2 space-y-1">
                    <li>Resend (envoi d'emails)</li>
                    <li>MongoDB Atlas (hébergement des données)</li>
                  </ul>
                </li>
                <li><strong>Autorités compétentes :</strong> en cas d'obligation légale</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Nous ne vendons jamais vos données personnelles à des tiers à des fins de marketing direct.
              </p>
            </section>

            {/* Transferts internationaux */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                7. Transferts internationaux
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Certains de nos prestataires peuvent traiter des données en dehors de l'Espace Économique Européen (EEE). Dans ce cas, nous nous assurons que des garanties appropriées sont en place, notamment les Clauses Contractuelles Types de la Commission européenne ou la certification au cadre de protection des données UE-États-Unis (EU-US Data Privacy Framework).
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                8. Cookies
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Notre site utilise des cookies pour améliorer votre expérience :
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Finalité</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">Cookies essentiels</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Fonctionnement du site, authentification</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Session / 7 jours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-slate-600">Cookies analytiques</td>
                      <td className="px-4 py-3 text-sm text-slate-600">Statistiques de visite anonymisées</td>
                      <td className="px-4 py-3 text-sm text-slate-600">13 mois</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.
              </p>
            </section>

            {/* Conservation */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                9. Durée de conservation
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Nous conservons vos données personnelles pendant les durées suivantes :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Demandes de devis :</strong> 3 ans après la dernière interaction</li>
                <li><strong>Comptes entrepreneurs :</strong> durée de la relation commerciale + 5 ans (obligations comptables)</li>
                <li><strong>Données de paiement :</strong> 10 ans (obligations fiscales belges)</li>
                <li><strong>Logs de connexion :</strong> 1 an</li>
              </ul>
            </section>

            {/* Droits */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                10. Vos droits
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Conformément au RGPD et à la loi belge, vous disposez des droits suivants :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Droit d'accès (Art. 15) :</strong> obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification (Art. 16) :</strong> corriger des données inexactes ou incomplètes</li>
                <li><strong>Droit à l'effacement (Art. 17) :</strong> demander la suppression de vos données ("droit à l'oubli")</li>
                <li><strong>Droit à la limitation (Art. 18) :</strong> restreindre le traitement de vos données</li>
                <li><strong>Droit à la portabilité (Art. 20) :</strong> recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition (Art. 21) :</strong> vous opposer au traitement de vos données</li>
                <li><strong>Droit de retirer votre consentement :</strong> à tout moment, sans affecter la licéité du traitement antérieur</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a>
              </p>
              <p className="text-slate-600 leading-relaxed mt-2">
                Nous répondrons à votre demande dans un délai d'un mois. Ce délai peut être prolongé de deux mois en cas de demande complexe.
              </p>
            </section>

            {/* Sécurité */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                11. Sécurité des données
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Mots de passe hashés avec algorithme bcrypt</li>
                <li>Authentification par tokens JWT sécurisés</li>
                <li>Accès restreint aux données sur base du besoin d'en connaître</li>
                <li>Sauvegardes régulières et sécurisées</li>
                <li>Surveillance et détection des intrusions</li>
              </ul>
            </section>

            {/* Mineurs */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                12. Protection des mineurs
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nos services ne s'adressent pas aux personnes de moins de 18 ans. Nous ne collectons pas sciemment de données personnelles concernant des mineurs. Si nous découvrons que nous avons collecté des données d'un mineur, nous les supprimerons immédiatement.
              </p>
            </section>

            {/* Réclamation */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                13. Droit de réclamation
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de l'Autorité de protection des données belge :
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Autorité de protection des données (APD)</p>
                <p className="text-slate-600">Rue de la Presse 35, 1000 Bruxelles</p>
                <p className="text-slate-600">Téléphone : +32 2 274 48 00</p>
                <p className="text-slate-600">Email : contact@apd-gba.be</p>
                <p className="text-slate-600">Site web : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">www.autoriteprotectiondonnees.be</a></p>
              </div>
            </section>

            {/* Modifications */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                14. Modifications de la politique
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Les modifications seront publiées sur cette page avec une nouvelle date de mise à jour. En cas de modifications substantielles, nous vous en informerons par email ou via une notification sur notre site.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                15. Contact
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles, contactez-nous :
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Propriétaire du site : Stéphane Muller (personne physique)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant : Muller Construction SRL</p>
                <p className="text-slate-600">N° BCE / TVA : BE 0803.324.50</p>
                <p className="text-slate-600">Email : <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a></p>
                <p className="text-slate-600">Téléphone : +32 61 46 83 32</p>
                <p className="text-slate-600">Adresse : Bertrix, Belgique</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
